import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { Linking } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  Pressable,
  Box,
  ScrollView,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { FacilityLogo } from "../../../features/Session/components/FacilityLogo";
import { HomeTabsNavigatorParamList } from "../../../navigators/HomeTabsNavigator/types";
import { useFlags } from "../../../utils/launchDarkly";
import { AccountSettings } from "./AccountSettings";
import { ClubSettings } from "./ClubSettings";
import ProfileInfo from "./ProfileInfo";
import SignoutModal from "./SignoutModal";
import { ProfileQueryRendererQuery } from "./__generated__/ProfileQueryRendererQuery.graphql";

type ProfileQueryRendererProps = {
  queryRef: PreloadedQuery<ProfileQueryRendererQuery>;
};

export const ProfileQueryRenderer = ({
  queryRef,
}: ProfileQueryRendererProps) => {
  const data = usePreloadedQuery(ProfileQueryRendererQueryString, queryRef);
  return <ProfileQueryRendererContent data={data} />;
};

type ProfileQueryRendererContentProps = {
  data: ProfileQueryRendererQuery["response"];
};

const ProfileQueryRendererContent = ({
  data,
}: ProfileQueryRendererContentProps) => {
  const club = data.session?.currentClientProfile?.member?.club;
  const user = data.session?.user;
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isAccountSettingsModalOpen, setIsAccountSettingsModalOpen] =
    useState(false);

  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const isSmallScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    eventCardColumn: false,
    lg: false,
    xl: false,
  });

  const { newTopbar } = useFlags();

  return (
    <Box
      bg="surface-primary"
      flex={1}
      safeAreaTop={newTopbar ? undefined : true}
    >
      <ScrollView
        flex={1}
        showsVerticalScrollIndicator={false}
        bg="grayscale-0"
        pt={{ base: 0, lg: 6 }}
        contentContainerStyle={{ paddingBottom: 100, flexGrow: 1 }}
      >
        <Box flex={1} width="100%" mb={10}>
          <Container flex={1}>
            <ScreenHeader
              includeHorizontalPadding={false}
              title={`${user?.firstName || ""} ${user?.lastName || ""}`}
              subHeading={newTopbar ? undefined : t`Profile`}
              leftItem={
                newTopbar ? null : (
                  <Button
                    variant="secondary"
                    leftIcon={<ArrowLeftIcon />}
                    onPress={() => navigation.navigate("MainHome")}
                    testID="mobile-header-back-button"
                    accessibilityLabel={t`Back button`}
                  />
                )
              }
              rightItem={
                club && !newTopbar ? (
                  <Pressable onPress={() => navigation.navigate("MainHome")}>
                    <FacilityLogo
                      imageProps={{
                        style: { width: 56, height: 56, borderRadius: 8 },
                      }}
                      clubRef={club}
                    />
                  </Pressable>
                ) : null
              }
              testID="profile-screen-agenda-header"
            />

            {data.session?.currentClientProfile ? (
              <ProfileInfo
                fragmentRef={data.session?.currentClientProfile}
                setEditMode={() => setIsAccountSettingsModalOpen(true)}
              />
            ) : null}
          </Container>
          <Container>
            <HStack
              flex={1}
              flexDirection={{ md: "row", base: "column" }}
              gap={isSmallScreen ? 10 : 24}
              mt={4}
            >
              {data.session?.currentClientProfile?.member?.club ? (
                <ClubSettings
                  fragmentRef={data.session?.currentClientProfile?.member?.club}
                  customerRef={data.session?.currentClientProfile?.member}
                />
              ) : null}

              {data.session?.currentClientProfile?.member?.club ? (
                <AccountSettings
                  setIsSignOutModalOpen={setIsConfirmationModalOpen}
                  isOpen={isAccountSettingsModalOpen}
                  setIsOpen={setIsAccountSettingsModalOpen}
                  fragmentRef={data.session?.currentClientProfile?.member?.club}
                />
              ) : null}
            </HStack>
          </Container>
        </Box>

        <Container>
          <Text
            variant="mainSmallSemiBold"
            color="text-main-primary"
            fontWeight="500"
          >
            {t`Learn more about`}{" "}
            <Text
              accessibilityRole="link"
              color="text-main-primary"
              textDecorationLine="underline"
              onPress={() => Linking.openURL("https://whoosh.io/terms")}
            >
              {t`Whoosh Terms and Conditions`}
            </Text>{" "}
            {t`and`}{" "}
            <Text
              accessibilityRole="link"
              color="text-main-primary"
              textDecorationLine="underline"
              onPress={() => Linking.openURL("https://whoosh.io/privacy")}
            >
              {t`Privacy Policy`}
            </Text>
          </Text>
        </Container>
      </ScrollView>

      <SignoutModal
        {...{
          isOpen: isConfirmationModalOpen,
          setIsOpen: setIsConfirmationModalOpen,
        }}
      />
    </Box>
  );
};

export const ProfileQueryRendererQueryString = graphql`
  query ProfileQueryRendererQuery($patronLoyalty: Boolean!) {
    session {
      user {
        id
        email
        ... on MemberUser {
          id
          firstName
          lastName
          email
          phoneNumber {
            nationalNumber
          }
        }
      }
      currentClientProfile {
        ...ProfileInfo_ClientProfile
        member {
          ...ClubSettings_customer @arguments(patronLoyalty: $patronLoyalty)
          club {
            name
            ...FacilityLogoFragment_club
            ...ClubSettings_Club @arguments(patronLoyalty: $patronLoyalty)
            ...AccountSettings_Club
          }
        }
      }
    }
  }
`;
